import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, Image as ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { getGalleryItemBySlug, getGalleryItems } from '../lib/cms';

export default function GalleryDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [otherProjects, setOtherProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    async function loadProjectData() {
      setLoading(true);
      try {
        const data = await getGalleryItemBySlug(slug);
        if (!data) {
          // Hvis projektet ikke findes, prøv at hente alle og find ved ID eller gå tilbage
          const allProjects = await getGalleryItems();
          const found = allProjects.find(p => p.id === slug || p.slug?._ === slug);
          if (found) {
            setProject(found);
          } else {
            navigate('/galleri');
            return;
          }
        } else {
          setProject(data);
        }

        // Hent andre projekter til sidebaren
        const all = await getGalleryItems();
        setOtherProjects(all.filter(p => (p.slug?._ || p.id) !== slug).slice(0, 4));
        setActiveImageIndex(0);
      } catch (err) {
        console.error('Fejl ved indlæsning af galleriprojekt:', err);
        navigate('/galleri');
      } finally {
        setLoading(false);
      }
    }
    loadProjectData();
  }, [slug, navigate]);

  // SEO Titel og Meta Description for galleriprojektet
  useEffect(() => {
    if (!project) return;

    const title = `${project.title} | Referencer | Toa Byg ApS`;
    const description = project.description || `Se billeder og detaljer fra byggeprojektet ${project.title} udført af Toa Byg ApS i Ishøj og på Sjælland.`;

    document.title = title;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);
  }, [project]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!project) return null;

  const images = project.image || [];

  const nextImage = () => {
    setActiveImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevImage = () => {
    setActiveImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Hero Banner */}
      <section className="relative bg-blue-950 text-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={images[0]}
            alt={project.title}
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-blue-950 via-blue-950/80 to-blue-950" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/galleri"
            className="inline-flex items-center text-sm font-semibold text-white hover:text-slate-200 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Tilbage til galleriet
          </Link>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight uppercase">{project.title}</h1>
          <p className="mt-4 text-lg text-slate-300 max-w-3xl leading-relaxed">
            Se detaljer og billeder fra dette udførte projekt.
          </p>
        </div>
      </section>

      {/* Indhold & Sidebar */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Venstre: Billedgalleri & Beskrivelse */}
            <div className="lg:col-span-2 space-y-8">
              {/* Stort aktivt billede */}
              <div className="relative aspect-w-16 aspect-h-10 bg-slate-100 overflow-hidden rounded-none border border-slate-200 shadow-sm group">
                {images.length > 0 ? (
                  <>
                    <img
                      src={images[activeImageIndex]}
                      alt={`${project.title} - Billede ${activeImageIndex + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {images.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-blue-600 hover:text-white text-slate-800 p-2 rounded-none transition-all shadow-md focus:outline-none"
                          aria-label="Forrige billede"
                        >
                          <ChevronLeft className="h-6 w-6" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-blue-600 hover:text-white text-slate-800 p-2 rounded-none transition-all shadow-md focus:outline-none"
                          aria-label="Næste billede"
                        >
                          <ChevronRight className="h-6 w-6" />
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center p-12 text-slate-400">
                    <ImageIcon className="h-12 w-12 mb-2" />
                    <span>Intet billede tilgængeligt</span>
                  </div>
                )}
              </div>

              {/* Små billeder (Thumbnails) */}
              {images.length > 1 && (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-4">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImageIndex(idx)}
                      className={`aspect-w-1 aspect-h-1 rounded-none overflow-hidden border-2 transition-all ${
                        idx === activeImageIndex
                          ? 'border-blue-600 ring-2 ring-blue-600/20'
                          : 'border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${project.title} thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Projektbeskrivelse */}
              <div className="bg-white p-8 sm:p-10 border border-slate-200 shadow-sm rounded-none">
                <h3 className="text-2xl font-extrabold text-slate-900 uppercase tracking-wider mb-4">
                  Om projektet
                </h3>
                <p className="text-base text-slate-600 leading-relaxed whitespace-pre-line">
                  {project.description || 'Der er ingen yderligere beskrivelse tilgængelig for dette projekt.'}
                </p>
                
                <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-bold text-slate-900 uppercase block">Lokation</span>
                    <span className="text-slate-600">Ishøj & omegn</span>
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 uppercase block">Kategori</span>
                    <span className="text-slate-600 uppercase tracking-wider">
                      {project.terms?.['gallery-category']?.[0] || 'Håndværk'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Højre: Sidebar */}
            <div className="space-y-8">
              {/* Andre Projekter */}
              <div className="bg-white rounded-none p-6 shadow-sm border border-slate-100">
                <h3 className="text-lg font-bold text-slate-900 mb-4 pb-3 border-b border-slate-100 uppercase">
                  Andre projekter
                </h3>
                <ul className="space-y-3">
                  {otherProjects.map((p) => (
                    <li key={p.id}>
                      <Link
                        to={`/galleri/${p.slug?._ || p.id}`}
                        className="flex items-center justify-between p-3 rounded-none hover:bg-blue-50 text-slate-700 hover:text-blue-600 font-medium transition-all text-sm uppercase tracking-wider"
                      >
                        <span className="truncate mr-2">{p.title}</span>
                        <span className="text-blue-600 shrink-0">→</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Hurtig Kontakt */}
              <div className="bg-blue-900 text-white rounded-none p-6 shadow-sm relative overflow-hidden">
                <div className="absolute -top-12 -right-12 w-24 h-24 bg-blue-800 rounded-full opacity-30" />
                <h3 className="text-xl font-bold mb-3 uppercase">Interesseret i noget lignende?</h3>
                <p className="text-blue-100 text-sm mb-6 leading-relaxed">
                  Går du med overvejelser om et lignende projekt? Vi står klar med professionel rådgivning og et uforpligtende tilbud.
                </p>
                <div className="space-y-3">
                  <Link
                    to="/kontakt"
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-xs font-bold rounded-none text-blue-900 bg-white hover:bg-blue-50 transition-colors shadow-md uppercase tracking-wider text-center"
                  >
                    Få et uforpligtende tilbud
                  </Link>
                  <a
                    href="tel:+4540942420"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-blue-400 text-xs font-bold rounded-none text-white bg-transparent hover:bg-blue-800 transition-colors uppercase tracking-wider text-center"
                  >
                    <Phone className="h-4 w-4 text-blue-300 shrink-0" />
                    <span>Ring +45 40 94 24 20</span>
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
